import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { IQWatchDeviceWithData, RouterStatus } from "../../types";
import { DeviceFilter, setSort } from "../../store/dashboardSlice.ts";
import { getRouterStatus } from "../../services/iqwatchService.ts";
import {
  Battery,
  Wifi,
  Signal,
  MapPin,
  Building2,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import batteryEmptyIcon from "../../assets/battery-empty-svgrepo-com.svg";
import batteryLowIcon from "../../assets/battery-low-svgrepo-com.svg";
import batteryMidIcon from "../../assets/battery-mid-svgrepo-com.svg";
import batteryFullIcon from "../../assets/battery-full-svgrepo-com.svg";

type SortField =
  | "deviceName"
  | "organization"
  | "site"
  | "status"
  | "batterySoc"
  | "batteryVoltage"
  | "solarPower"
  | "loadPower";

interface IQWatchDeviceTableProps {
  devices: IQWatchDeviceWithData[];
  onDeviceDoubleClick?: (deviceWithData: IQWatchDeviceWithData) => void;
  showSiteColumn?: boolean; // Add prop to show site column
  showOrganizationColumn?: boolean; // Add prop to show organization column
  deviceFilter?: DeviceFilter; // Add device filter prop
}

const IQWatchDeviceTable: React.FC<IQWatchDeviceTableProps> = ({
  devices,
  onDeviceDoubleClick,
  showSiteColumn = false,
  showOrganizationColumn = false,
  deviceFilter = "all",
}) => {
  const dispatch = useDispatch();
  const { sortField, sortOrder } = useSelector(
    (state: RootState) => state.dashboard
  );

  // Store router status for each device by device ID
  const [routerStatusMap, setRouterStatusMap] = useState<
    Record<string, RouterStatus | null>
  >({});
  const [loadingRouters, setLoadingRouters] = useState<Set<string>>(new Set());
  const [failedRouters, setFailedRouters] = useState<Set<string>>(new Set());

  // Fetch router status for devices that have router_serial_number
  useEffect(() => {
    const fetchRouterStatuses = async () => {
      const devicesToFetch = devices.filter(
        (d) =>
          d.device.router_serial_number &&
          !routerStatusMap[d.device.id] &&
          !loadingRouters.has(d.device.id) &&
          !failedRouters.has(d.device.id)
      );

      if (devicesToFetch.length === 0) return;

      const fetchPromises = devicesToFetch.map(async (deviceWithData) => {
        const device = deviceWithData.device;
        if (!device.router_serial_number) return;

        setLoadingRouters((prev) => new Set(prev).add(device.id));
        try {
          const status = await getRouterStatus({
            serial: device.router_serial_number,
          });
          setRouterStatusMap((prev) => ({
            ...prev,
            [device.id]: status,
          }));
        } catch (error) {
          // Only log non-404 errors, 404s are expected for routers that don't exist
          if ((error as any)?.response?.status !== 404) {
            console.error(
              `Failed to fetch router status for device ${device.id}:`,
              error
            );
          }
          // Mark as failed so we don't retry immediately
          setFailedRouters((prev) => new Set(prev).add(device.id));
        } finally {
          setLoadingRouters((prev) => {
            const next = new Set(prev);
            next.delete(device.id);
            return next;
          });
        }
      });

      await Promise.all(fetchPromises);
    };

    fetchRouterStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices]); // Only depend on devices, not routerStatusMap to avoid infinite loops

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null
      if (sortOrder === "asc") {
        dispatch(setSort({ field, order: "desc" }));
      } else if (sortOrder === "desc") {
        dispatch(setSort({ field: null, order: null }));
      }
    } else {
      dispatch(setSort({ field, order: "asc" }));
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return null;
    }
    if (sortOrder === "asc") {
      return <ChevronUp className="w-4 h-4 ml-1 inline" />;
    } else if (sortOrder === "desc") {
      return <ChevronDown className="w-4 h-4 ml-1 inline" />;
    }
    return null;
  };

  // Devices are already filtered from the API, so we just return them as-is
  // No client-side filtering needed anymore (search and filter are done server-side)
  const filteredDevices = useMemo(() => {
    return devices;
  }, [devices]);

  // Devices are already sorted from the API, so we only need to filter
  // No client-side sorting needed anymore
  const sortedDevices = useMemo(() => {
    return filteredDevices;
  }, [filteredDevices]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "running":
        return "bg-green-100 text-green-600";
      case "stopped":
        return "bg-yellow-100 text-yellow-600";
      case "error":
        return "bg-red-100 text-red-600";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const getSignalQuality = (rssi: number) => {
    if (rssi >= -50) return { quality: "Excellent", color: "text-green-600" };
    if (rssi >= -60) return { quality: "Good", color: "text-green-500" };
    if (rssi >= -70) return { quality: "Fair", color: "text-yellow-600" };
    return { quality: "Poor", color: "text-red-600" };
  };

  const getConnectionStatusColor = (connstate: string) => {
    switch (connstate?.toLowerCase()) {
      case "connected":
        return "text-green-600";
      case "disconnected":
        return "text-red-600";
      case "connecting":
        return "text-yellow-600";
      default:
        return "text-gray-500";
    }
  };

  const getBatterySocColor = (
    soc: number | string | null | undefined
  ): string => {
    if (soc === null || soc === undefined) return "text-gray-600";
    const socValue = typeof soc === "string" ? parseFloat(soc) : soc || 0;

    if (socValue >= 0 && socValue < 30) {
      return "text-red-600";
    } else if (socValue >= 30 && socValue < 60) {
      return "text-amber-600";
    } else if (socValue >= 60 && socValue <= 100) {
      return "text-green-600";
    }
    return "text-gray-600";
  };

  const getBatteryIcon = (soc: number | string | null | undefined) => {
    if (soc === null || soc === undefined) {
      return (
        <img
          src={batteryEmptyIcon}
          alt="battery"
          className="w-5 h-5 mr-1 flex-shrink-0"
          style={{ filter: "none" }}
        />
      );
    }
    const socValue = typeof soc === "string" ? parseFloat(soc) : soc || 0;
    const color = getBatterySocColor(soc);

    // Get the appropriate icon based on SOC level
    let iconSrc: string;
    if (socValue >= 0 && socValue < 30) {
      iconSrc = batteryLowIcon;
    } else if (socValue >= 30 && socValue < 60) {
      iconSrc = batteryMidIcon;
    } else if (socValue >= 60 && socValue <= 100) {
      iconSrc = batteryFullIcon;
    } else {
      iconSrc = batteryEmptyIcon;
    }

    // Convert color class to hex for CSS filter
    const getColorFilter = (colorClass: string) => {
      if (colorClass === "text-red-600") {
        return "invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)";
      } else if (colorClass === "text-amber-600") {
        return "invert(70%) sepia(88%) saturate(1234%) hue-rotate(1deg) brightness(102%) contrast(95%)";
      } else if (colorClass === "text-green-600") {
        return "invert(48%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(118%) contrast(119%)";
      }
      return "none";
    };

    return (
      <img
        src={iconSrc}
        alt="battery"
        className="w-5 h-5 mr-1 flex-shrink-0"
        style={{ filter: getColorFilter(color) }}
      />
    );
  };

  const formatBatteryVoltage = (
    voltage: string | number | null | undefined
  ) => {
    if (voltage === null || voltage === undefined) return "N/A";
    const num = typeof voltage === "string" ? parseFloat(voltage) : voltage;
    return isNaN(num) ? "N/A" : `${num.toFixed(2)}V`;
  };

  const formatPower = (power: string | number | null | undefined) => {
    if (power === null || power === undefined) return "N/A";
    const num = typeof power === "string" ? parseFloat(power) : power;
    return isNaN(num) ? "N/A" : `${num.toFixed(1)}W`;
  };

  const formatSOC = (soc: string | number | null | undefined) => {
    if (soc === null || soc === undefined) return "N/A";
    const num = typeof soc === "string" ? parseFloat(soc) : soc;
    return isNaN(num) ? "N/A" : `${num.toFixed(1)}%`;
  };

  const formatDateTime = (dateTime: string | null | undefined) => {
    if (!dateTime) return "N/A";
    try {
      return new Date(dateTime).toLocaleString();
    } catch {
      return "N/A";
    }
  };

  return (
    <div className="w-full overflow-x-auto">
      <table
        className={`${
          showSiteColumn || showOrganizationColumn
            ? "min-w-[1400px]"
            : "min-w-[1200px]"
        } w-full`}
      >
        <thead className="bg-gray-50 border-b">
          <tr>
            <th
              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px] cursor-pointer hover:bg-gray-100 select-none"
              onClick={() => handleSort("deviceName")}
            >
              <div className="flex items-center">
                Device Name
                {getSortIcon("deviceName")}
              </div>
            </th>
            {showOrganizationColumn && (
              <th
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort("organization")}
              >
                <div className="flex items-center">
                  Organization
                  {getSortIcon("organization")}
                </div>
              </th>
            )}
            {showSiteColumn && (
              <th
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort("site")}
              >
                <div className="flex items-center">
                  Site
                  {getSortIcon("site")}
                </div>
              </th>
            )}
            <th
              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px] cursor-pointer hover:bg-gray-100 select-none"
              onClick={() => handleSort("status")}
            >
              <div className="flex items-center">
                Status
                {getSortIcon("status")}
              </div>
            </th>
            <th
              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[90px] cursor-pointer hover:bg-gray-100 select-none"
              onClick={() => handleSort("batterySoc")}
            >
              <div className="flex items-center">
                Battery SOC
                {getSortIcon("batterySoc")}
              </div>
            </th>
            <th
              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100 select-none"
              onClick={() => handleSort("batteryVoltage")}
            >
              <div className="flex items-center">
                Battery Voltage
                {getSortIcon("batteryVoltage")}
              </div>
            </th>
            <th
              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[90px] cursor-pointer hover:bg-gray-100 select-none"
              onClick={() => handleSort("solarPower")}
            >
              <div className="flex items-center">
                Solar Power
                {getSortIcon("solarPower")}
              </div>
            </th>
            <th
              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[90px] cursor-pointer hover:bg-gray-100 select-none"
              onClick={() => handleSort("loadPower")}
            >
              <div className="flex items-center">
                Load Power
                {getSortIcon("loadPower")}
              </div>
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
              Router Status
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[90px]">
              Signal
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[160px]">
              Last Data Updated
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sortedDevices.map((deviceWithData) => {
            const device = deviceWithData.device;
            const latestData = deviceWithData.latest_data;
            // Use real router status from API if available, otherwise fall back to router_data
            const routerStatus = routerStatusMap[device.id];
            const routerData = latestData?.router_data;

            return (
              <tr
                key={device.id}
                className="hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => onDeviceDoubleClick?.(deviceWithData)}
                title="Click to view details"
              >
                <td className="px-3 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <Battery className="w-4 h-4 text-blue-400 mr-2 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {device.name}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        ID: {device.iqwatch_device_id}
                      </div>
                    </div>
                  </div>
                </td>
                {showOrganizationColumn && (
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Building2 className="w-3 h-3 text-indigo-500 mr-1 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm text-gray-900 truncate">
                          {device.organization?.name || "Unknown Org"}
                        </div>
                      </div>
                    </div>
                  </td>
                )}
                {showSiteColumn && (
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <MapPin className="w-3 h-3 text-gray-400 mr-1 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm text-gray-900 truncate">
                          {device.site?.name || "Unknown Site"}
                        </div>
                      </div>
                    </div>
                  </td>
                )}
                <td className="px-3 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                      latestData?.status || device.status
                    )}`}
                  >
                    {latestData?.status || device.status}
                  </span>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm">
                  <div className="flex items-center">
                    {getBatteryIcon(latestData?.soc || device.last_soc)}
                    <span
                      className={getBatterySocColor(
                        latestData?.soc || device.last_soc
                      )}
                    >
                      {formatSOC(latestData?.soc || device.last_soc)}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatBatteryVoltage(
                    latestData?.battery_voltage || device.last_battery_voltage
                  )}
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatPower(
                    latestData?.solar_power || device.last_solar_power
                  )}
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatPower(
                    latestData?.load_power || device.last_load_power
                  )}
                </td>
                {/* Router Status Column */}
                <td className="px-3 py-4 whitespace-nowrap">
                  {loadingRouters.has(device.id) ? (
                    <span className="text-xs text-gray-400">Loading...</span>
                  ) : routerStatus && routerStatus !== null ? (
                    <div className="flex items-center space-x-2">
                      <Wifi className="w-4 h-4 text-blue-500" />
                      <div>
                        <div
                          className={`text-xs font-medium ${getConnectionStatusColor(
                            routerStatus.connstate
                          )}`}
                        >
                          {routerStatus.connstate}
                        </div>
                        <div className="text-xs text-gray-500">
                          {routerStatus.operator || "N/A"}
                        </div>
                      </div>
                    </div>
                  ) : routerData?.input2 ? (
                    <div className="flex items-center space-x-2">
                      <Wifi className="w-4 h-4 text-blue-500" />
                      <div>
                        <div
                          className={`text-xs font-medium ${getConnectionStatusColor(
                            routerData.input2.connstate
                          )}`}
                        >
                          {routerData.input2.connstate}
                        </div>
                        <div className="text-xs text-gray-500">
                          {routerData.input2.conntype}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">
                      {device.router_serial_number ? "No Data" : "No Router"}
                    </span>
                  )}
                </td>
                {/* Signal Quality Column */}
                <td className="px-3 py-4 whitespace-nowrap">
                  {loadingRouters.has(device.id) ? (
                    <span className="text-xs text-gray-400">Loading...</span>
                  ) : routerStatus &&
                    routerStatus !== null &&
                    routerStatus.rssi !== undefined ? (
                    <div className="flex items-center space-x-1">
                      <Signal className="w-4 h-4 text-gray-400" />
                      <div>
                        <div
                          className={`text-xs font-medium ${
                            getSignalQuality(routerStatus.rssi).color
                          }`}
                        >
                          {getSignalQuality(routerStatus.rssi).quality}
                        </div>
                        <div className="text-xs text-gray-500">
                          {routerStatus.rssi} dBm
                        </div>
                      </div>
                    </div>
                  ) : routerData?.input2?.rssi ? (
                    <div className="flex items-center space-x-1">
                      <Signal className="w-4 h-4 text-gray-400" />
                      <div>
                        <div
                          className={`text-xs font-medium ${
                            getSignalQuality(routerData.input2.rssi).color
                          }`}
                        >
                          {getSignalQuality(routerData.input2.rssi).quality}
                        </div>
                        <div className="text-xs text-gray-500">
                          {routerData.input2.rssi} dBm
                        </div>
                      </div>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">N/A</span>
                  )}
                </td>
                <td className="px-3 py-4 text-sm text-gray-500">
                  <div className="space-y-1">
                    <div className="text-xs">
                      {formatDateTime(device.last_data_timestamp)}
                    </div>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default IQWatchDeviceTable;
